import { useCallback, useMemo } from 'react';

import {
  useFiltersCount,
  useGQLSearchContext,
} from '@sorare/core/src/contexts/graphql-search';

import { SavedFiltersView } from '#src/search/SavedFilters/SavedFiltersView.tsx';
import type { CardFilters } from '#src/search/SavedFilters/updateCardFilters.ts';

export const BackendSavedFilters = () => {
  const ctx = useGQLSearchContext();
  const filtersCount = useFiltersCount();

  const query = ctx?.query;
  const activeFacets = ctx?.activeFacets;
  const urlToType = ctx?.urlToType;
  const setSearchParams = ctx?.setSearchParams;

  const currentUiState: CardFilters = useMemo(() => {
    const refinementList: Record<string, string[]> = {};
    const range: Record<string, string> = {};
    const menu: Record<string, string> = {};

    Object.entries(activeFacets ?? {}).forEach(([key, values]) => {
      if (!values?.length) return;
      const type = urlToType?.[key];
      if (type === 'numeric') {
        [range[key]] = values;
      } else if (type === 'menu') {
        [menu[key]] = values;
      } else {
        refinementList[key] = values;
      }
    });

    return {
      ...(query ? { query } : {}),
      toggle: {},
      refinementList,
      range,
      virtualToggle: {},
      menu,
    };
  }, [activeFacets, query, urlToType]);

  const applyFilters = useCallback(
    (filters: CardFilters) => {
      setSearchParams?.(prev => {
        prev.delete('q');
        prev.delete('p');
        Object.keys(activeFacets ?? {}).forEach(key => prev.delete(key));

        if (filters.query) {
          prev.set('q', filters.query);
        }
        Object.entries(filters.refinementList ?? {}).forEach(
          ([key, values]) => {
            if (values?.length) {
              prev.set(key, values.map(encodeURIComponent).join(','));
            }
          }
        );
        Object.entries(filters.menu ?? {}).forEach(([key, value]) => {
          if (value) prev.set(key, value);
        });
        Object.entries(filters.range ?? {}).forEach(([key, value]) => {
          if (value) prev.set(key, value);
        });
        return prev;
      });
    },
    [setSearchParams, activeFacets]
  );

  if (!ctx) return null;

  return (
    <SavedFiltersView
      currentUiState={currentUiState}
      filtersCount={filtersCount}
      applyFilters={applyFilters}
    />
  );
};
